/**
 * One graded cell of the Performance Matrix.
 *
 * Customer review 11 Sep (item 2): the cell shows the band's configured
 * label in its palette colour, with black or white text by contrast — the same
 * badge the Table's Rating column draws (`ratingBadgeStyle`). It printed the
 * grade letter in white on the band's raw colour, so a pale band (Platinum
 * #E5E4E2) was unreadable and a relabelled band still read "B".
 */
import { MatrixRatingBand } from '../../../lib/api';
import { ratingBadgeStyle } from '../utils';
import { matrixGradeLabel } from './exportMatrix';

/**
 * Fallback palette for responses without rating_bands (deploy lag).
 * CR-7079: aligned to the confirmed baseline.
 */
const FALLBACK_BAND_COLORS: Record<string, string> = {
  A: '#E5E4E2', B: '#D4AF37', C: '#C0C0C0',
  D: '#CD7F32', E: '#8C7853', F: '#000000',
};

interface MatrixRatingCellProps {
  rating: string;
  bands: MatrixRatingBand[];
  pct: number | null;
  score?: number | null;
  ppm?: number | null;
  qualityLabel: string;
}

export function MatrixRatingCell({ rating, bands, pct, score, ppm, qualityLabel }: MatrixRatingCellProps) {
  if (!rating || rating === '-') return null;
  const band = bands.find((b) => b.grade === rating);
  const colour = band?.color ?? FALLBACK_BAND_COLORS[rating] ?? '#9e9e9e';
  const titleParts: string[] = [];
  if (band) titleParts.push(`${band.label}${band.max_score !== undefined ? ` (${band.min_score}–${band.max_score})` : ''}`);
  if (pct !== null) titleParts.push(`OTIF ${pct.toFixed(1)}%`);
  if (ppm !== null && ppm !== undefined) titleParts.push(`${qualityLabel} ${ppm.toLocaleString()}`);
  if (score !== null && score !== undefined) titleParts.push(`Score ${score.toFixed(1)}`);
  return (
    <span
      className="inline-flex items-center justify-center font-bold text-xs rounded shadow-sm px-1.5"
      style={{ ...ratingBadgeStyle(colour), minWidth: '28px', height: '28px' }}
      title={titleParts.length > 0 ? titleParts.join(' · ') : undefined}
    >
      {matrixGradeLabel(rating, bands)}
    </span>
  );
}
